import React, { useContext, useEffect } from 'react';
import { Form } from 'react-final-form';

import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';
import { BUTTON_TYPE, SIZE, VARIANT } from '@motorway/mw-highway-code/enums';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { VALUATIONS_GA_TAGS } from 'Utilities/analytics';
import { HOMEPAGE_GA_TAGS } from 'Utilities/analytics/analyticsHomePage';
import { EVENT } from 'Utilities/analytics/optimizely/constants';
import { useOptimizelyTrack } from 'Utilities/analytics/optimizely/useOptimizelyTrack';
import { type HeroAnimationState } from 'Utilities/animation';
import { getMileage } from 'Utilities/mileage';

import { useSocialProofAuthUIExperiment } from 'Experiments/useSocialProofAuthUIExperiment/useSocialProofAuthUIExperiment';

import { useHeroMileageInputHelpers } from '../../../../../fastlane/hooks/useHeroMileageInputHelpers';
import { MileageInput } from '../../../../../fastlane/mileageInput/MileageInput';
import MileageInputHigh from '../../../../../fastlane/mileageInput/MileageInputHigh';
import LocalTexts from '../../HeroTexts.json';

import styles from './HeroInputMileageWrapper.scss';

const LocalT = new MDText(LocalTexts);

const t = (key: string, args?: Record<string, React.ReactNode>) => `${LocalT.translate(key, args)}`;

const TEXTS = {
  confirmMileage: t('cta.confirmMileage'),
  continue: t('cta.continue'),
};

export type HeroMileageProps = {
  isCarValueTracker?: boolean;
  loadingHandler: (state: HeroAnimationState) => void;
  onSubmit?: (mileage: number) => void;
  setShowMileage: (showMileage: boolean) => void;
  showMileage: boolean;
};

export const HeroInputMileageWrapper = ({
  loadingHandler, onSubmit, setShowMileage, showMileage,
}: HeroMileageProps) => {
  const { vehicleLookupState: lookupVehicle } = useContext(VehicleLookupContext);
  const { OPTIMIZELY_TRACK } = useOptimizelyTrack();
  const vehicleLookupMileage = getMileage({ vehicle: lookupVehicle });
  const vrm = lookupVehicle?.vrm ?? '';

  useSocialProofAuthUIExperiment(true);

  const {
    confirmedMileage, confirmMileageHelpers, onFormSubmit,
  } = useHeroMileageInputHelpers({
    loadingHandler,
    onSubmit,
    setShowMileage,
  });

  useEffect(() => {
    if (showMileage) {
      HOMEPAGE_GA_TAGS.HERO_MILEAGE_SHOWN(vrm, vehicleLookupMileage);
      VALUATIONS_GA_TAGS.HERO_VEHICLE_LOOKUP_COMPLETE();
      VALUATIONS_GA_TAGS.VEHICLE_LOOKUP_COMPLETE(vrm);
    }
  }, [showMileage, vrm, vehicleLookupMileage]);

  const ctaText = (vehicleLookupMileage ? TEXTS.confirmMileage : TEXTS.continue);

  return (
    <>
      <Form
        initialValues={{
          mileage: vehicleLookupMileage,
        }}
        onSubmit={({ mileage }) => {
          if (mileage !== vehicleLookupMileage) {
            OPTIMIZELY_TRACK(EVENT.VALUATIONS_MILEAGE_UPDATED);
          }

          onFormSubmit({ mileage });
        }}
        render={({ handleSubmit, submitting, values }) => (
          <form
            noValidate
            className={styles.component}
            onSubmit={handleSubmit}
          >
            <MileageInput isHero validateOnInit />
            <Button
              data-thc-button
              data-cy={'continueButton'}
              icon={'chevron'}
              label={ctaText}
              loading={submitting}
              onClick={() => {
                const mileage = values?.mileage;
                VALUATIONS_GA_TAGS.HERO_MILEAGE_BUTTON_CLICKED_SNOWPLOW({ mileage, vrm });
              }}
              size={SIZE.LARGE}
              type={BUTTON_TYPE.SUBMIT}
              variant={VARIANT.PRIMARY}
            />
          </form>
        )}
      />
      {
        confirmedMileage
        && (<MileageInputHigh
          confirmMileage={confirmMileageHelpers()}
          modalClassName={styles.highMileageModalHeadings}
          {...confirmedMileage}
        />)
      }
    </>
  );
};
